import type { ReactNode, KeyboardEvent } from 'react';
import { useRef, useEffect } from 'react';

import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Popover,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridDensity,
} from '@mui/x-data-grid-pro';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';

export interface ViewOptionsPopoverProps {
  /** Unique ID for the popover */
  id?: string;
  /** Whether the popover is open */
  open: boolean;
  /** Anchor element for popover positioning */
  anchorEl: HTMLElement | null;
  /** Callback when popover should close */
  onClose: () => void;
  /** Array of column definitions (optional for views without columns) */
  columns?: GridColDef[];
  /** Current column visibility model (optional for views without columns) */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** Callback when column visibility toggle is clicked (optional for views without columns) */
  onColumnToggle?: (field: string) => () => void;
  /** Current density setting (optional for views without density) */
  density?: GridDensity;
  /** Callback when density changes (optional for views without density) */
  onDensityChange?: (density: GridDensity) => void;
  /** Optional: Additional content to render at the top */
  renderHeader?: () => ReactNode;
  /** Optional: Additional content to render at the bottom */
  renderFooter?: () => ReactNode;
}

/**
 * Reusable popover component for view options
 * Conditionally renders controls based on props provided:
 * - Density selection (Compact/Standard) - if density/onDensityChange provided
 * - Column/field visibility toggles - if columns/columnVisibilityModel/onColumnToggle provided
 * - Custom sections via renderHeader/renderFooter slots
 *
 * IMPORTANT: This uses Popover instead of Menu
 * ============================================
 * While MUI's Menu component provides built-in keyboard navigation, it was not suitable
 * for this use case because:
 *
 * 1. Menu expects MenuItem children, but this component contains form controls
 *    (RadioGroup, Checkboxes) which are not menu items.
 *
 * 2. Menu's MenuList component uses "roving tabindex" for keyboard navigation, which
 *    conflicts with form controls' native keyboard behavior:
 *    - Prevents Tab key from working properly (can't tab between controls)
 *    - Interferes with RadioGroup's native arrow key navigation
 *    - Captures keyboard events meant for form controls
 *
 * 3. Form controls (RadioGroup, Checkbox) have their own ARIA roles and keyboard
 *    behavior that should not be overridden by MenuItem's role="menuitem".
 *
 * Solution: Use Popover with custom keyboard navigation
 * ======================================================
 * - Popover provides the positioning and backdrop without keyboard management
 * - Custom focus management: Auto-focus first element on open
 * - Custom arrow key navigation for checkboxes (RadioGroup has native support)
 * - Escape key closes popover and returns focus to trigger
 * - All form controls maintain their native Tab, Arrow, and Space key behavior
 *
 * This approach provides Menu-like keyboard navigation while preserving the native
 * behavior of form controls, resulting in better accessibility and user experience.
 */

export const ViewOptionsPopover = ({
  id,
  open,
  anchorEl,
  onClose,
  columns,
  columnVisibilityModel,
  onColumnToggle,
  density,
  onDensityChange,
  renderHeader,
  renderFooter,
}: ViewOptionsPopoverProps) => {
  const { t } = useTranslation('views');
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      // Small timeout to ensure popover is fully rendered
      const timer = window.setTimeout(() => {
        // Find the first focusable input element (radio or checkbox)
        const firstInput = popoverRef?.current?.querySelector<HTMLInputElement>(
          'input[type="radio"], input[type="checkbox"]',
        );

        firstInput?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      // Focus will automatically return to trigger button via Popover
      return;
    }

    // Handle arrow key navigation for checkboxes
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const target = event.target as HTMLElement;

      // Only handle arrow keys if we're on a checkbox (not radio, which has native behavior)
      if (target.getAttribute('type') === 'checkbox') {
        event.preventDefault();

        // Find all checkbox inputs
        const checkboxes = Array.from(
          popoverRef.current?.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"]',
          ) || [],
        );

        const currentIndex = checkboxes.indexOf(target as HTMLInputElement);

        if (currentIndex !== -1) {
          let nextIndex: number;

          if (event.key === 'ArrowDown') {
            // Modulo wraps to first checkbox when reaching the end
            nextIndex = (currentIndex + 1) % checkboxes.length;
          } else {
            nextIndex =
              currentIndex === 0 ? checkboxes.length - 1 : currentIndex - 1;
          }

          checkboxes[nextIndex]?.focus();
        }
      }
    }
  };

  return (
    <Popover
      id={id}
      role="dialog"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          onKeyDown: handleKeyDown,
          ref: popoverRef,
        },
      }}
    >
      <Paper padded>
        <Stack gap={2}>
          {renderHeader?.()}

          {/* Density Selector - only show if density props provided */}
          {density !== undefined && onDensityChange && (
            <>
              <FormControl component="fieldset">
                <Stack>
                  <FormLabel component="legend">{t('DensityLabel')}</FormLabel>
                  <RadioGroup
                    value={density}
                    onChange={(e) =>
                      onDensityChange(e.target.value as GridDensity)
                    }
                  >
                    <FormControlLabel
                      value="compact"
                      control={<Radio size="small" />}
                      label={t('Compact')}
                    />
                    <FormControlLabel
                      value="standard"
                      control={<Radio size="small" />}
                      label={t('Standard')}
                    />
                  </RadioGroup>
                </Stack>
              </FormControl>

              {columns && columns.length > 0 && <Divider />}
            </>
          )}

          {/* Column Visibility - only show if columns props provided */}
          {columns &&
            columns.length > 0 &&
            columnVisibilityModel &&
            onColumnToggle && (
              <Stack>
                <FormLabel component="legend">{t('ChooseColumns')}</FormLabel>
                <Stack gap={0}>
                  {columns
                    .filter((col) => col.hideable !== false)
                    .map((col) => (
                      <FormControlLabel
                        key={col.field}
                        control={
                          <Checkbox
                            checked={columnVisibilityModel[col.field] ?? true}
                            onChange={onColumnToggle(col.field)}
                            size="small"
                          />
                        }
                        label={col.headerName}
                      />
                    ))}
                </Stack>
              </Stack>
            )}

          {renderFooter?.()}
        </Stack>
      </Paper>
    </Popover>
  );
};
